import { defineStore } from 'pinia'
import Vue from 'vue'

export const useAppUsageRestrictionsStore = defineStore('appUsageRestrictions', {
    state: () => ({
        restrictions: [],
        restrictionsInitialState: [],
        currentDeviceId: null
    }),

    actions: {
        setRestrictions(restrictions) {
            this.restrictions = restrictions
        },

        setRestrictionsInitialState(restrictions) {
            this.restrictionsInitialState = JSON.parse(JSON.stringify(restrictions))
        },

        setCurrentDeviceId(deviceId) {
            this.currentDeviceId = deviceId
        },

        /**
         * Represents a mapping of device identifiers to their respective app usage restriction data.
         *
         * @type {Object.<string, Object>}
         *
         * Example structure:
         * {
         *   "device_id_1": {
         *     rules: [AppUsageRule, AppUsageRule, ...],
         *     appInstallationViaPlayStore: { minutesAllowed: 45, createdAt: "..." },
         *     hotspotSharing: { enabled: true, activeIndefinitely: false, minutesAllowed: 30, createdAt: "...", startedAt: "" }
         *   },
         *   "device_id_2": {
         *     rules: [AppUsageRule, ...],
         *     appInstallationViaPlayStore: null,
         *     hotspotSharing: null
         *   }
         * }
         */

        /**
         * @typedef {Object} AppUsageRule
         * @property {number} id - Unique rule identifier
         * @property {string} ruleName - Human-readable label for the rule
         * @property {'exclusive' | 'quota'} type - Type of rule:
         *           - 'exclusive': app usage allowed only in specific time window
         *           - 'quota': app usage limited by time per day/week
         *
         * @property {Array<AppInfo|string>} apps - List of apps affected by the rule.
         *           - Can be full objects with `appName` and `packageId`, or just package ID strings
         *
         * @property {string} startTime - Start time for app usage (format: 'HH:mm')
         * @property {string} endTime - End time for app usage (format: 'HH:mm')
         *
         * @property {number|null} [allowedTime=null] - Allowed usage time in minutes during the window. `null` means unlimited.
         * @property {number|null} [maxByDay=null] - Maximum allowed time per day in minutes (for 'quota' type)
         * @property {number|null} [maxByWeek=null] - Maximum allowed time per week in minutes (for 'quota' type)
         * @property {Array<number>} [weekDays] - Days of the week rule applies (1=Sunday ... 7=Saturday)
         * @property {boolean|number|null} [strictEnd] - If 1/true, app forcibly closes at `endTime`. Optional.
         */

        /**
         * @typedef {Object} AppInfo
         * @property {string} appName - Human-readable app name
         * @property {string} packageId - Unique app package identifier
         */
        getRestrictions() {
            return this.restrictions
        },

        getRestrictionsInitialState() {
            return this.restrictionsInitialState
        },

        getRulesForDevice(mdmDeviceId) {
            return this.restrictions[mdmDeviceId]?.rules || []
        },

        getAppInstallationViaPlayStoreForDevice(mdmDeviceId) {
            return this.restrictions[mdmDeviceId]?.appInstallationViaPlayStore || null
        },

        setAppInstallationViaPlayStoreForDevice(mdmDeviceId, data) {
            if (!this.restrictions[mdmDeviceId]) {
                Vue.set(this.restrictions, mdmDeviceId, { rules: [], appInstallationViaPlayStore: null, hotspotSharing: null })
            }
            // Ensure reactivity for the specific property
            Vue.set(this.restrictions[mdmDeviceId], 'appInstallationViaPlayStore', data)
        },

        deleteAppInstallationViaPlayStoreForDevice(mdmDeviceId) {
            if (this.restrictions[mdmDeviceId]) {
                Vue.set(this.restrictions[mdmDeviceId], 'appInstallationViaPlayStore', null)
            }
        },

        getHotspotSharingForDevice(mdmDeviceId) {
            return this.restrictions[mdmDeviceId]?.hotspotSharing || null
        },

        setHotspotSharingForDevice(mdmDeviceId, data) {
            if (!this.restrictions[mdmDeviceId]) {
                Vue.set(this.restrictions, mdmDeviceId, { rules: [], appInstallationViaPlayStore: null, hotspotSharing: null })
            }
            // Ensure reactivity for the specific property
            Vue.set(this.restrictions[mdmDeviceId], 'hotspotSharing', data)
        },

        deleteHotspotSharingForDevice(mdmDeviceId) {
            if (this.restrictions[mdmDeviceId]) {
                Vue.set(this.restrictions[mdmDeviceId], 'hotspotSharing', null)
            }
        }
    }
})
